import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";

import { keys, useDeleteJob, useExpenses, useJob, useRules, useSaveJob, useSettingsPage } from "../api/hooks";
import type { DayFx, EntryData, HmrcCity, JobDetail, JobRates } from "../api/types";
import { errorText, useFlash } from "../components/Flash";
import { HmrcCityPicker } from "../components/HmrcCityPicker";
import { LoadError, Loading } from "../components/Layout";
import { TickTile } from "../components/TickTile";
import {
  CHECK_LABELS,
  CHECKS,
  entryAmounts,
  fxFor,
  MEAL_LABELS,
  MEALS,
  ratesFor,
  type CheckKey,
  type MealKey,
} from "../lib/expenses";
import { cityFields, currencySymbol, HMRC_FIELDS } from "../lib/hmrc";
import { addDays, dlong, money, num, todayISO, toNum } from "../lib/format";
import { useTickDrag } from "../lib/useTickDrag";

const NUMS = ["mileage", "train", "taxi", "parking", "hotel_nights", "extra"] as const;
type NumKey = (typeof NUMS)[number];
/** The Travel columns: [field, heading, label for screen readers]. Miles, then fares in pounds. */
const TRAVEL: [NumKey, string, string][] = [
  ["mileage", "Miles", "Miles"],
  ["train", "Train £", "Train"],
  ["taxi", "Taxi £", "Taxi"],
  ["parking", "Parking £", "Parking"],
];

/**
 * A day row as edited: numbers kept as strings while typing. Meal allowances are tickboxes but keep
 * their count, so a day imported with 2 stays 2 unless it's unticked.
 */
type Row = { key: number; date: string; note: string } & Record<NumKey, string> &
  Record<CheckKey, boolean> &
  Record<MealKey, number>;

let rowKey = 1;
const toRow = (e: Partial<EntryData>): Row => ({
  key: rowKey++,
  date: e.date ?? "",
  note: e.note ?? "",
  ...(Object.fromEntries(NUMS.map((k) => [k, e[k] ? String(e[k]) : ""])) as Record<NumKey, string>),
  ...(Object.fromEntries(CHECKS.map((k) => [k, Number(e[k]) > 0])) as Record<CheckKey, boolean>),
  ...(Object.fromEntries(MEALS.map((k) => [k, Number(e[k]) || 0])) as Record<MealKey, number>),
});
const toEntry = (r: Row): EntryData => ({
  date: r.date,
  note: r.note.trim() || null,
  ...(Object.fromEntries(NUMS.map((k) => [k, toNum(r[k])])) as Record<NumKey, number>),
  ...(Object.fromEntries(CHECKS.map((k) => [k, r[k] ? 1 : 0])) as Record<CheckKey, number>),
  ...(Object.fromEntries(MEALS.map((k) => [k, r[k]])) as Record<MealKey, number>),
});

export function JobPage() {
  const id = Number(useParams().id);
  const q = useJob(id);
  if (q.isPending) return <Loading />;
  if (q.error) return <LoadError error={q.error} />;
  return <JobEditor key={id} detail={q.data} />;
}

function JobEditor({ detail }: { detail: JobDetail }) {
  const { job: j0, year } = detail;
  const settings = useSettingsPage(year);
  const save = useSaveJob(j0.id);
  const del = useDeleteJob();
  const expenses = useExpenses();
  const flash = useFlash();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [job, setJob] = useState({
    name: j0.name,
    role: j0.role ?? "",
    notes: j0.notes ?? "",
    concur_code: j0.concur_code ?? "",
    hmrc_city: j0.hmrc_city ?? "",
    hmrc_currency: j0.hmrc_currency ?? "",
    drink_rate: String(j0.drink_rate),
    lunch_rate: String(j0.lunch_rate),
    dinner_rate: String(j0.dinner_rate),
    hotel_rate: String(j0.hotel_rate),
    overtime_rate: j0.overtime_rate === null ? "" : String(j0.overtime_rate),
  });
  const [paysOvertime, setPaysOvertime] = useState(j0.pays_overtime);
  const defaultOvertime = useRules().data?.overtime_rate ?? 1;
  const [rows, setRows] = useState<Row[]>(() => detail.entries.map(toRow));
  const paint = useTickDrag();
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const touch = () => {
    setDirty(true);
    setStatus("Unsaved changes");
  };
  const setJobField = (k: keyof typeof job) => (e: { target: { value: string } }) => {
    setJob((x) => ({ ...x, [k]: e.target.value }));
    touch();
  };
  const pickCity = (c: HmrcCity | null) => {
    setJob((x) => ({ ...x, ...cityFields(c) }));
    touch();
  };
  const setCell = (key: number, k: keyof Row, v: string | boolean | number) => {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, [k]: v } : r)));
    touch();
  };

  const rates: JobRates = {
    drink_rate: toNum(job.drink_rate),
    lunch_rate: toNum(job.lunch_rate),
    dinner_rate: toNum(job.dinner_rate),
    hotel_rate: toNum(job.hotel_rate),
  };
  const currency = job.hmrc_currency || null;
  // The server sent rates for the saved currency; after switching currency they arrive on save.
  const dayFx = (date: string) =>
    currency && currency === (j0.hmrc_currency ?? null) && date ? fxFor(detail.fx, date) : undefined;
  const computed = rows.map(
    (r) =>
      entryAmounts(
        toEntry(r),
        { ...rates, hmrc_currency: currency },
        ratesFor(detail.rates, r.date),
        dayFx(r.date)?.rate,
      ).total,
  );
  const sum = (k: NumKey) => rows.reduce((a, r) => a + toNum(r[k]), 0);
  // Hotel nights only appear for years that claimed them (a hotel rate in Settings) or days that have some.
  const hotelRate = (date: string) => ratesFor(detail.rates, date)?.hotel_night_rate ?? 0;
  const showHotel =
    Object.values(detail.rates).some((r) => r.hotel_night_rate > 0) ||
    rows.some((r) => toNum(r.hotel_nights) > 0);
  const grand = computed.reduce((a, b) => a + b, 0);

  const addDay = () => {
    const dates = rows
      .map((r) => r.date)
      .filter(Boolean)
      .sort();
    const next = dates.length ? addDays(dates[dates.length - 1], 1) : todayISO();
    setRows((rs) => [...rs, toRow({ date: next })]);
    touch();
  };

  const onSave = () => {
    if (rows.some((r) => !r.date)) return setStatus("Every day needs a date.");
    setStatus("Saving…");
    save.mutate(
      {
        job: {
          name: job.name,
          role: job.role || null,
          hmrc_city: job.hmrc_city || null,
          hmrc_currency: currency,
          notes: job.notes || null,
          concur_code: job.concur_code.trim() || null,
          pays_overtime: paysOvertime,
          overtime_rate: job.overtime_rate.trim() ? toNum(job.overtime_rate) : null,
          ...rates,
        },
        entries: rows.map(toEntry),
      },
      {
        onSuccess: async () => {
          setDirty(false);
          // Load the fresh list first, so it doesn't flash the old totals.
          await qc.refetchQueries({ queryKey: keys.jobs(year), type: "all" });
          flash("Job saved.");
          navigate(`/year/${year}/jobs`);
          window.scrollTo(0, 0);
        },
        onError: (err) => setStatus(errorText(err)),
      },
    );
  };

  const onDelete = () => {
    if (!window.confirm(`Delete ${j0.name} and all its days? This can't be undone.`)) return;
    del.mutate(j0.id, {
      onSuccess: () => {
        setDirty(false);
        flash("Job deleted.");
        navigate(`/year/${year}/jobs`);
      },
      onError: (err) => flash(errorText(err), "error"),
    });
  };

  const roles = settings.data?.roles ?? [];
  return (
    <>
      <div className="page-head">
        <h1>{job.name || j0.name}</h1>
        <Link className="small" to={`/year/${year}/jobs`}>
          All {year} jobs
        </Link>
      </div>

      <div className="panel">
        <div className="fields">
          <div className="wide">
            <label htmlFor="j-name">Job name</label>
            <input id="j-name" value={job.name} onChange={setJobField("name")} />
          </div>
          <div>
            <label htmlFor="j-role">Role</label>
            <select id="j-role" value={job.role} onChange={setJobField("role")}>
              {roles.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.name}
                </option>
              ))}
              {job.role && !roles.some((r) => r.code === job.role) && (
                <option value={job.role}>{job.role}</option>
              )}
              <option value="">No role</option>
            </select>
          </div>
          {expenses && (
            <div>
              <label htmlFor="j-concur-code">Concur job code</label>
              <input
                id="j-concur-code"
                value={job.concur_code}
                maxLength={40}
                onChange={setJobField("concur_code")}
                title="The Job/Dept/Capex for this job's expense claim in Concur"
              />
            </div>
          )}
          {expenses && (
            <div className="wide">
              <HmrcCityPicker id="j-hmrc-city" value={job.hmrc_city} onPick={pickCity} />
            </div>
          )}
          <div className="job-overtime">
            <label htmlFor="j-overtime-rate">Overtime (× hourly rate)</label>
            <div className="job-overtime-row">
              <label className="check" title="Whether this job pays its overtime">
                <input
                  type="checkbox"
                  checked={paysOvertime}
                  onChange={(e) => {
                    setPaysOvertime(e.target.checked);
                    touch();
                  }}
                />
                Paid
              </label>
              <input
                id="j-overtime-rate"
                type="number"
                min="0.1"
                max="5"
                step="0.05"
                disabled={!paysOvertime}
                placeholder={`Default (×${num(defaultOvertime)})`}
                value={job.overtime_rate}
                onChange={setJobField("overtime_rate")}
              />
            </div>
          </div>
          {expenses &&
            HMRC_FIELDS.map(([k, , label]) => (
              <div key={k}>
                <label htmlFor={`j-${k}`}>
                  HMRC {label.toLowerCase()} ({currencySymbol(currency)})
                </label>
                <input
                  id={`j-${k}`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={job[k]}
                  onChange={setJobField(k)}
                />
              </div>
            ))}
          <div className="wide">
            <label htmlFor="j-notes">Notes</label>
            <input id="j-notes" value={job.notes} onChange={setJobField("notes")} />
          </div>
        </div>

        <h2 style={{ marginTop: "1.25rem" }}>Days</h2>
        {expenses && (
          <p className="small muted">
            Tick each meal allowance claimed. HMRC boxes use this job's rates above
            {currency && ", converted to pounds at each day's exchange rate"}. Rates for mileage and meal
            allowances come from <Link to={`/year/${year}/settings`}>Settings</Link>.
          </p>
        )}
        <div className="table-wrap">
          <table className="days-table">
            <thead>
              {expenses && (
                <tr className="groups">
                  <th />
                  <th colSpan={TRAVEL.length} className="group">
                    Travel
                  </th>
                  <th colSpan={4} className="group">
                    Meal allowance
                  </th>
                  <th colSpan={CHECKS.length} className="group">
                    HMRC
                  </th>
                  {showHotel && <th />}
                  <th colSpan={currency ? 4 : 3} />
                </tr>
              )}
              <tr>
                <th>Date</th>
                {expenses && (
                  <>
                    {TRAVEL.map(([k, heading]) => (
                      <th key={k} className="box-head">
                        {heading}
                      </th>
                    ))}
                    {MEALS.map((k) => (
                      <th key={k} className="tick-head">
                        {MEAL_LABELS[k]}
                      </th>
                    ))}
                    {CHECKS.map((k) => (
                      <th key={k} className="tick-head">
                        {CHECK_LABELS[k]}
                      </th>
                    ))}
                    {showHotel && <th className="box-head">Hotel nights</th>}
                    <th className="box-head">Extra £</th>
                  </>
                )}
                {expenses && currency && (
                  <th className="num" title="The exchange rate for each day">
                    £1 =
                  </th>
                )}
                {expenses && <th className="num">Total</th>}
                <th>
                  <span className="visually-hidden">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.key}>
                  <td className="c-date" data-label="Date">
                    <input
                      type="date"
                      required
                      value={r.date}
                      onChange={(e) => setCell(r.key, "date", e.target.value)}
                    />
                  </td>
                  {expenses && (
                    <>
                      {TRAVEL.map(([k, heading, label]) => (
                        <td key={k} className="c-num" data-label={heading}>
                          <input
                            type="number"
                            min="0"
                            step={k === "mileage" ? "any" : "0.01"}
                            aria-label={label}
                            value={r[k]}
                            onChange={(e) => setCell(r.key, k, e.target.value)}
                          />
                        </td>
                      ))}
                      {MEALS.map((k) => (
                        <td key={k} className="tick-cell c-meal" data-label={MEAL_LABELS[k]}>
                          <TickTile
                            on={r[k] > 0}
                            label={`Meal allowance ${MEAL_LABELS[k]}`}
                            title={r[k] > 1 ? `Claimed ${r[k]} times` : undefined}
                            set={(v) => setCell(r.key, k, v ? 1 : 0)}
                            paint={paint}
                          />
                        </td>
                      ))}
                      {CHECKS.map((k) => (
                        <td key={k} className="tick-cell c-hmrc" data-label={CHECK_LABELS[k]}>
                          <TickTile
                            on={r[k]}
                            label={`HMRC ${CHECK_LABELS[k].toLowerCase()}`}
                            set={(v) => setCell(r.key, k, v)}
                            paint={paint}
                          />
                        </td>
                      ))}
                      {showHotel && (
                        <td className="c-num" data-label="Hotel nights">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            aria-label="Hotel nights"
                            title={`${money(hotelRate(r.date))} per night`}
                            value={r.hotel_nights}
                            onChange={(e) => setCell(r.key, "hotel_nights", e.target.value)}
                          />
                        </td>
                      )}
                      <td className="c-num" data-label="Extra £">
                        <input
                          type="number"
                          step="0.01"
                          aria-label="Extra"
                          value={r.extra}
                          onChange={(e) => setCell(r.key, "extra", e.target.value)}
                        />
                      </td>
                    </>
                  )}
                  {expenses && currency && <FxCell fx={dayFx(r.date)} currency={currency} />}
                  {expenses && (
                    <td className="num row-total c-total" data-label="Day total">
                      {money(computed[i])}
                    </td>
                  )}
                  <td className="c-remove">
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label="Remove this day"
                      onClick={() => {
                        setRows((rs) => rs.filter((x) => x.key !== r.key));
                        touch();
                      }}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {expenses && (
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td className="num box-total">
                    {sum("mileage") ? Math.round(sum("mileage") * 10) / 10 : ""}
                  </td>
                  {TRAVEL.slice(1).map(([k]) => (
                    <td key={k} className="num box-total">
                      {sum(k) ? money(sum(k)) : ""}
                    </td>
                  ))}
                  <td colSpan={MEALS.length} />
                  <td colSpan={CHECKS.length} />
                  {showHotel && <td className="num box-total">{sum("hotel_nights") || ""}</td>}
                  <td className="num box-total">{sum("extra") ? money(sum("extra")) : ""}</td>
                  {currency && <td />}
                  <td className="num">{money(grand)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="actions">
          <button type="button" className="btn quiet" onClick={addDay}>
            Add the next day
          </button>
        </div>

        <div className="savebar">
          <button type="button" className="btn" onClick={onSave} disabled={save.isPending}>
            Save job
          </button>
          <span className="status" role="status">
            {status}
          </span>
          {expenses && <span className="grand">{money(grand)}</span>}
        </div>
      </div>

      <div style={{ marginTop: "1.25rem" }}>
        <button className="btn danger" type="button" onClick={onDelete} disabled={del.isPending}>
          Delete job
        </button>
      </div>
    </>
  );
}

/** The exchange rate a day uses. Provisional rates (a nearby day's, until this day's is published) get a "≈". */
function FxCell({ fx, currency }: { fx: DayFx | undefined; currency: string }) {
  if (!fx)
    return (
      <td
        className="num fx-cell muted c-fx"
        data-label="£1 ="
        title="The exchange rate is looked up when you save"
      >
        on save
      </td>
    );
  const text = `${fx.exact ? "" : "≈ "}${fx.rate.toFixed(4)} ${currencySymbol(currency)}`;
  const title = fx.exact
    ? `Exchange rate for ${dlong(fx.date)}`
    : `Provisional: the ${dlong(fx.date)} rate, until this day's rate is published`;
  return (
    <td className={`num fx-cell c-fx${fx.exact ? "" : " muted"}`} data-label="£1 =" title={title}>
      {text}
    </td>
  );
}
